/**
 * The daily-digest settings, and the record of what was actually sent.
 *
 * Two different kinds of fact, kept apart. The settings are configuration a
 * person edits. The run history is `email_runs`, written only by the scheduler
 * and readable only by whoever holds `integrations.manage` — a client write
 * there could never be mistaken for evidence that anything was sent, because
 * a client cannot write there at all.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { EmailRunStatus } from '@timely/domain';
import { supabase } from '../lib/supabaseClient';
import { apiPost } from '../lib/api';

export interface AlertSettings {
    dailyEmail: boolean;
    sendAtLocal: string;
    recipientRoleIds: string[];
    extraRecipients: string[];
    skipWhenEmpty: boolean;
    updatedAt: string | null;
}

const DEFAULTS: AlertSettings = {
    dailyEmail: false, sendAtLocal: '09:00', recipientRoleIds: [], extraRecipients: [],
    skipWhenEmpty: true, updatedAt: null,
};

interface SettingsRow {
    daily_email: boolean;
    send_at_local: string;
    recipient_role_ids: string[];
    extra_recipients: string[];
    skip_when_empty: boolean;
    updated_at: string;
}

export interface EmailRun {
    id: string;
    runDate: string;
    kind: 'daily_digest' | 'test';
    status: EmailRunStatus;
    errorCategory: string | null;
    detail: string | null;
    /** The moment worth showing: when it finished, else started, else was queued. */
    at: string;
    recipients: number;
    delivered: number;
    failed: number;
    skipped: number;
    attemptCount: number;
    maxAttempts: number;
    nextAttemptAt: string | null;
}

interface RunRow {
    id: string; run_date: string; kind: 'daily_digest' | 'test'; status: EmailRunStatus;
    error_category: string | null; detail: string | null;
    created_at: string; started_at: string | null; finished_at: string | null; next_attempt_at: string | null;
    recipients: number; delivered: number; failed: number; skipped: number;
    attempt_count: number; max_attempts: number;
}

export interface RecipientPreview {
    memberRecipients: number;
    unmatchedExtras: number;
}

/** History is shown a page at a time: it grows by a row a day, forever. */
export const RUN_PAGE = 10;

const RUN_COLUMNS = 'id, run_date, kind, status, error_category, detail, created_at, started_at, finished_at, '
    + 'next_attempt_at, recipients, delivered, failed, skipped, attempt_count, max_attempts';

const toRun = (r: RunRow): EmailRun => ({
    id: r.id, runDate: r.run_date, kind: r.kind, status: r.status,
    errorCategory: r.error_category, detail: r.detail,
    at: r.finished_at ?? r.started_at ?? r.created_at,
    recipients: r.recipients, delivered: r.delivered, failed: r.failed, skipped: r.skipped,
    attemptCount: r.attempt_count, maxAttempts: r.max_attempts,
    nextAttemptAt: r.status === 'pending' ? r.next_attempt_at : null,
});

async function fetchRuns(workspaceId: string, offset: number) {
    // One more than a page, to know whether there is another without counting.
    return supabase()
        .from('email_runs')
        .select(RUN_COLUMNS)
        .eq('workspace_id', workspaceId)
        .order('created_at', { ascending: false })
        .order('id', { ascending: false })
        .range(offset, offset + RUN_PAGE);
}

export function useAlertSettings(workspaceId: string) {
    const [settings, setSettings] = useState<AlertSettings>(DEFAULTS);
    const [runs, setRuns] = useState<EmailRun[]>([]);
    const [hasMoreRuns, setHasMoreRuns] = useState(false);
    const [runsError, setRunsError] = useState<string | null>(null);
    const [preview, setPreview] = useState<RecipientPreview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const issuedFor = useRef(workspaceId);

    const load = useCallback(async () => {
        issuedFor.current = workspaceId;
        setLoading(true);
        setError(null);
        setRunsError(null);
        const client = supabase();
        const [settingsResult, runsResult, previewResult] = await Promise.all([
            client.from('workspace_alert_settings')
                .select('daily_email, send_at_local, recipient_role_ids, extra_recipients, skip_when_empty, updated_at')
                .eq('workspace_id', workspaceId).maybeSingle(),
            fetchRuns(workspaceId, 0),
            client.rpc('digest_recipient_preview', { p_workspace: workspaceId }),
        ]);
        if (issuedFor.current !== workspaceId) return;

        if (settingsResult.error) {
            setError('We could not load your alert settings. Try again in a moment.');
        } else if (settingsResult.data) {
            const r = settingsResult.data as SettingsRow;
            setSettings({
                dailyEmail: r.daily_email, sendAtLocal: r.send_at_local.slice(0, 5),
                recipientRoleIds: r.recipient_role_ids ?? [], extraRecipients: r.extra_recipients ?? [],
                skipWhenEmpty: r.skip_when_empty, updatedAt: r.updated_at,
            });
        } else {
            setSettings(DEFAULTS);
        }

        if (runsResult.error) {
            // An unreadable history is said to be unreadable — never shown as
            // "nothing has been sent", which would be a claim.
            setRuns([]);
            setHasMoreRuns(false);
            setRunsError('We could not load the send history just now.');
        } else {
            const rows = (runsResult.data ?? []) as unknown as RunRow[];
            setRuns(rows.slice(0, RUN_PAGE).map(toRun));
            setHasMoreRuns(rows.length > RUN_PAGE);
        }

        const p = Array.isArray(previewResult.data) ? previewResult.data[0] : previewResult.data;
        setPreview(!previewResult.error && p
            ? { memberRecipients: Number((p as Record<string, unknown>)['member_recipients']) || 0,
                unmatchedExtras: Number((p as Record<string, unknown>)['unmatched_extras']) || 0 }
            : null);
        setLoading(false);
    }, [workspaceId]);

    const loadMoreRuns = useCallback(async () => {
        const result = await fetchRuns(workspaceId, runs.length);
        if (issuedFor.current !== workspaceId) return;
        if (result.error) {
            setRunsError('We could not load more of the send history just now.');
            return;
        }
        const rows = (result.data ?? []) as unknown as RunRow[];
        setRuns(current => [...current, ...rows.slice(0, RUN_PAGE).map(toRun)]);
        setHasMoreRuns(rows.length > RUN_PAGE);
    }, [workspaceId, runs.length]);

    useEffect(() => { void load(); }, [load]);

    return { settings, runs, hasMoreRuns, runsError, preview, loading, error, reload: load, loadMoreRuns };
}

export class AlertSettingsError extends Error {}

export async function saveAlertSettings(workspaceId: string, settings: AlertSettings): Promise<void> {
    if (!/^\d{2}:\d{2}$/.test(settings.sendAtLocal)) {
        throw new AlertSettingsError('Choose a valid send time.');
    }
    const badEmail = settings.extraRecipients.find(e => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    if (badEmail) throw new AlertSettingsError(`"${badEmail}" is not a valid email address.`);
    if (settings.extraRecipients.length > 25) throw new AlertSettingsError('At most 25 extra recipients.');

    const { error } = await supabase()
        .from('workspace_alert_settings')
        .upsert({
            workspace_id: workspaceId,
            daily_email: settings.dailyEmail,
            send_at_local: `${settings.sendAtLocal}:00`,
            recipient_role_ids: settings.recipientRoleIds,
            extra_recipients: settings.extraRecipients,
            skip_when_empty: settings.skipWhenEmpty,
        }, { onConflict: 'workspace_id' });

    if (error) {
        throw new AlertSettingsError(
            /row-level security|permission denied/i.test(error.message)
                ? 'You do not have permission to change alert settings.'
                : /invalid recipient address/i.test(error.message)
                    ? 'One of the extra addresses is not a valid email address.'
                    : /not a role of this company/i.test(error.message)
                        ? 'One of the chosen roles no longer exists. Refresh and try again.'
                        : 'We could not save that. Try again in a moment.',
        );
    }
}

export interface TestSendResult {
    runId: string;
    status: EmailRunStatus | 'error';
    category: string | null;
    to: string;
}

/** A test digest, to the signed-in person only. The server decides the address. */
export const sendTestDigest = (workspaceId: string) =>
    apiPost<TestSendResult>('/api/alerts/test-send', { workspaceId });
